import getConfig from 'next/config';

const {
  publicRuntimeConfig: { PLATFORM_PREFIX },
} = getConfig();

const routePaths = {
  BASE: '',
  INDEX: '/',
  ERROR: '/error',
  HEALTH: '/health',
  NEXT_ERROR: '/_error',
  FOOTER: {
    CONTACT_US: '/contact-us',
    // @todo avoid using special characters on urls (currently is set up with in db)
    FAQS: '/faqs',
    PRODUCT_POINTS: '/product-points',
  },
  PAGES: {
    CAMPAIGN: '/campaign',
    CAMPAIGN_DETAIL: '/campaign/[id]',
    CONTACT_REP: '/contact-rep',
    EXPERIENCE: '/experience',
    FORMATION: '/formation',
    CHALLENGE: '/challenge',
    DONATION: '/experience',
    CHALLENGE_DETAIL: '/challenge/[id]',
    EXPERIENCE_CHECKOUT_END: '/experience/checkout-end',
    EXPERIENCE_CHECKOUT_PAYMENT_END: '/experience/checkout-payment-end',
    DONATION_CHECKOUT_END: '/donation/checkout-donation-end',
    TIMIFY_END: '/timify/timify-end',
    EXPERIENCE_DETAIL: '/experience/[id]',
    FORMATION_DETAIL: '/formation/[id]',
    DONATION_DETAIL: '/experience/[id]',
    LOGIN: '/login',
    MY_ACCOUNT_EDIT: '/accountEdit',
    MY_ORDERS: '/orders',
    RECOVERY_CHANGE: '/recovery',
    RECOVERY_PASSWORD: '/recovery-password',
    SIGNUP: '/register',
    TALK: '/talk',
    TALK_DETAIL: '/talk/[id]',
    TRAINING: '/training',
    TRAINING_DETAIL: '/training/[id]',
    REQUEST_ORDER: '/request-order',
    MARKETING_MATERIALS: '/marketing-materials',
    SURVEY: '/survey',
    SURVEY_DYNAMIC: '/survey/[id]',
    SCAN: {
      SCAN_INDEX: '/scan',
      SCAN_ERROR: '/scan/error',
    },
    MY_ACCOUNT: {
      ACCOUNT_INDEX: '/account',
      MY_PROFILE: '/account/my-profile',
      MY_POINTS: '/account/my-points',
      MY_BONUS: '/account/my-bonus',
      HISTORY: '/account/points-historical',
      ORDERS: '/account/orders',
      MY_ADDRESSES: '/account/my-addresses',
      COMPLETE_PROFILE: '/account/complete-profile',
      MY_PHARMACY: '/account/my-pharmacy',
      MY_ACADEMIC_RECORD: '/account/my-academic-record',
    },
    MY_CENTER: {
      CENTER_INDEX: '/center',
      CENTER_PROFILE: '/center/profile',
      CENTER_MEMBERS: '/center/members',
      MY_INVOICES: '/center/invoices',
    },
    QR_LANDING: '/qr-love-isdin',
    VALIDATE_ACCOUNT: '/validate-account',
  },
  IMPERSONATE: '/impersonate',
  STATIC: '/static',
};

export const staticRoutes = {
  STATIC_PAGES: {
    GET_MORE_POINTS: '/obtener-puntos',
    PRODUCTS: '/productos',
  },
  FOOTER: {
    COOKIES_POLICY: '/política-cookies',
    PRIVACY_POLICY: '/politica-privacidad',
    TERMS: '/terms-conditions',
  },
};

// TODO: Remove all related cause now are static pages
export const deprecatedRoutePaths = {
  PAGES: {
    TALKS: '/talks',
    TRAININGS: '/trainings',
  },
};

export const publicPaths = [
  routePaths.BASE,
  routePaths.ERROR,
  routePaths.FOOTER.CONTACT_US,
  routePaths.FOOTER.FAQS,
  routePaths.FOOTER.PRODUCT_POINTS,
  routePaths.HEALTH,
  routePaths.IMPERSONATE,
  routePaths.INDEX,
  routePaths.NEXT_ERROR,
  routePaths.PAGES.LOGIN,
  routePaths.PAGES.QR_LANDING,
  routePaths.PAGES.RECOVERY_PASSWORD,
  routePaths.PAGES.SIGNUP,
  routePaths.PAGES.VALIDATE_ACCOUNT,
  routePaths.STATIC,
];

const challengePages = [
  routePaths.PAGES.CAMPAIGN_DETAIL,
  routePaths.PAGES.CHALLENGE_DETAIL,
  routePaths.PAGES.FORMATION_DETAIL,
  routePaths.PAGES.DONATION_DETAIL,
  routePaths.PAGES.EXPERIENCE_DETAIL,
  routePaths.PAGES.TALK_DETAIL,
  routePaths.PAGES.TRAINING_DETAIL,
];

export const checkRoute = (originalRoute: string, routes: object): boolean => {
  let included = false;

  for (const routeKey in routes) {
    const route = routes[routeKey];

    if (typeof route === 'string') {
      if (route.includes(originalRoute)) included = true;
    } else {
      const objectIncluded = checkRoute(originalRoute, route);
      if (objectIncluded) included = true;
    }
  }

  return included;
};

export const cleanRoute = (originalRoute: string) => {
  let baseRoute: string = originalRoute.replace(PLATFORM_PREFIX, '');

  if (baseRoute.includes('?')) {
    baseRoute = baseRoute.split('?')[0];
  }

  return baseRoute;
};

export const isChallengeDetail = (route: string) => {
  return challengePages.some((page) =>
    route.includes(page.replace('[id]', '')),
  );
};

export const isHome = (route: string) => {
  return route === routePaths.INDEX;
};

export const isExperiences = (route: string) => {
  return route === routePaths.PAGES.EXPERIENCE;
};

export const isScanPage = (route: string) => {
  return route === routePaths.PAGES.SCAN.SCAN_INDEX;
};

export const isSurveyDetail = (route: string) => {
  return route.includes(routePaths.PAGES.SURVEY_DYNAMIC.replace('[id]', ''));
};

export const isRouteDefined = (route: string) => {
  const routeCleaned = cleanRoute(route);
  const isDefined =
    checkRoute(routeCleaned, {
      ...deprecatedRoutePaths.PAGES,
      ...routePaths,
    }) || isChallengeDetail(routeCleaned);

  return isDefined;
};

export const isBasePath = (pathname: string) => {
  if (pathname == routePaths.BASE) return true;

  return pathname.length > 0 && pathname[0].includes('?');
};

export const isPage = (route: string) => {
  return Object.values(routePaths.PAGES).includes(route);
};

export const isAccountPage = (route: string) => {
  return Object.values(routePaths.PAGES.MY_ACCOUNT).includes(route);
};

export const isMyBonusPage = (route: string) => {
  return Object.values(routePaths.PAGES.MY_ACCOUNT.MY_BONUS).includes(route);
};

export const isStaticPage = (route: string) => {
  return routePaths.STATIC === route;
};

export const isChallengePage = (route: string) => {
  return routePaths.PAGES.CHALLENGE === route;
};

export const extractParamsFromChallengeDetail = (
  requestRoute: string,
): [string, string] | null => {
  let detailParams: [string, string] = null;

  challengePages.forEach((page) => {
    const challengeDetailRoute = page.replace('[id]', '');

    if (requestRoute.includes(challengeDetailRoute)) {
      const pathname = page;
      const [, id] = requestRoute.split(`${challengeDetailRoute}`);
      detailParams = [pathname.replace('[id]', id), id];
    }
  });

  return detailParams;
};

export default routePaths;
